/**
 * StickyHeaderContext
 *
 * Ermöglicht es Seiten, einen sticky Header zu definieren, der direkt
 * im AppLayout (als Kind des Scroll-Containers) gerendert wird.
 *
 * WARUM: CSS `position: sticky` funktioniert nicht zuverlässig in
 * verschachtelten Flex-Containern. Der sticky Header muss ein
 * direktes Kind des Scroll-Containers (app-layout-main) sein.
 *
 * @example
 * ```tsx
 * // In einer Seite:
 * useStickyHeader(<FilterBar />);
 *
 * // Oder via PageLayout:
 * <PageLayout stickyHeader={<FilterBar />}>
 *   <Content />
 * </PageLayout>
 * ```
 */

import type { ReactNode} from 'react';
import { createContext, useContext, useState, useCallback, useLayoutEffect, useRef } from 'react';

interface StickyHeaderContextType {
  /** Der aktuelle sticky header content */
  stickyHeader: ReactNode | null;
  /** Setzt den sticky header content */
  setStickyHeader: (content: ReactNode | null) => void;
  /** Registriert eine Komponente die den Header setzt */
  register: () => () => void;
}

const StickyHeaderContext = createContext<StickyHeaderContextType | null>(null);

export function StickyHeaderProvider({ children }: { children: ReactNode }) {
  const [stickyHeader, setStickyHeaderState] = useState<ReactNode | null>(null);
  // Track how many components have registered - only clear when count reaches 0
  const registrationCount = useRef(0);
  // Track pending clear timeout
  const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setStickyHeader = useCallback((content: ReactNode | null) => {
    // Cancel any pending clear when new content is set
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    setStickyHeaderState(content);
  }, []);

  const register = useCallback(() => {
    registrationCount.current += 1;
    // Cancel any pending clear
    if (clearTimeoutRef.current) {
      clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
    // Return unregister function
    return () => {
      registrationCount.current -= 1;
      // Only clear header if no components are registered anymore
      // Use a microtask delay to allow new components to register first
      // (handles the case where one PageLayout unmounts and another mounts)
      if (registrationCount.current === 0) {
        clearTimeoutRef.current = setTimeout(() => {
          // Double-check count is still 0 before clearing
          if (registrationCount.current === 0) {
            setStickyHeaderState(null);
          }
          clearTimeoutRef.current = null;
        }, 0);
      }
    };
  }, []);

  return (
    <StickyHeaderContext.Provider value={{ stickyHeader, setStickyHeader, register }}>
      {children}
    </StickyHeaderContext.Provider>
  );
}

/**
 * Hook zum Setzen eines sticky Headers.
 * Verwendet useLayoutEffect für synchrone Updates vor dem Paint.
 * Der Header wird automatisch beim Unmount entfernt.
 *
 * Funktioniert gracefully auch außerhalb des Providers (tut dann einfach nichts).
 */
export function useStickyHeader(content: ReactNode | null) {
  const context = useContext(StickyHeaderContext);
  const unregisterRef = useRef<(() => void) | null>(null);

  // Registration effect - runs only on mount/unmount
  useLayoutEffect(() => {
    // Gracefully handle missing provider - just don't register
    if (!context) return;

    // Register on mount
    unregisterRef.current = context.register();

    // Cleanup on unmount only
    return () => {
      if (unregisterRef.current) {
        unregisterRef.current();
        unregisterRef.current = null;
      }
    };
  }, [context]); // Only depends on context, not content

  // Content update effect - runs when content changes
  useLayoutEffect(() => {
    // Gracefully handle missing provider
    if (!context) return;
    context.setStickyHeader(content);
  }, [content, context]);
}

/**
 * Hook zum Abrufen des sticky header Inhalts (für AppLayout)
 */
export function useStickyHeaderContent() {
  const context = useContext(StickyHeaderContext);
  return context?.stickyHeader ?? null;
}

export { StickyHeaderContext };
